import React from 'react';
import "./HealthcareBentoGrid.css";
import { CardData } from '../BentoGridModal/BentoGridModal';
import ChartInterative from '../RecruitingBentoGrid/RecruitingBentoComponents/ChartInterative';
import SpeedInteractive from '../RecruitingBentoGrid/RecruitingBentoComponents/SpeedInteractive';
import CredentialTracker from '../RecruitingBentoGrid/RecruitingBentoComponents/CredentialTracker';
import PhoneAnimation from '../RecruitingBentoGrid/RecruitingBentoComponents/PhoneAnimation';
import { useIsMobile } from '../../../../custom-hooks/useIsMobile';

const HC_MOCK_CARDS: readonly CardData[] = [
    {
        title: 'Fill Every Shift. Every Time.',
        subtitle: 'Scale shift coverage without adding a single coordinator.',
        buttonOne: 'Explore Operations ›',
        buttonTwo: 'Book a demo',
        points: [
            'Fill urgent shifts in under 2 hours with auto-matching',
            'Assign workers by credential, availability, and proximity',
            'Real-time clock-in/out tracking across all care sites',
            'Payroll-ready timesheets generated automatically',
        ],
        insideComponent: null,
    },
    {
        title: 'Every Worker. Verified. Every Shift.',
        subtitle: 'Every nurse, PSW, and aide arrives credentialed, compliant, and cleared before they ever step on site.',
        buttonOne: 'See How It Works ›',
        buttonTwo: 'Book a demo',
        points: [
            'Licence and certification verified before first shift',
            'HIPAA-aligned data handling and audit trails',
            'Automated expiry alerts — no manual tracking',
            'Compliance documentation generated per worker',
        ],
        insideComponent: null,
    },
    {
        title: 'Win Healthcare Contracts. Keep Them.',
        subtitle: 'Land new facility agreements and own every renewal with built-in performance reporting.',
        buttonOne: 'Explore Sales ›',
        buttonTwo: 'Book a demo',
        points: [
            'CRM built specifically for staffing agency sales cycles',
            'Auto-generate client performance reports each month',
            'Track placements, fill rates, and client satisfaction scores',
            'Revenue forecasting by location and contract tier',
        ],
        insideComponent: null,
    },
];

const HealthcareBentoGrid = (): React.ReactElement => {
    const isMobile: boolean = useIsMobile();

    return (
        <div className="hc-wrapper">
                <div className="hc-grid">
                    <div className="bento-card hc-card-1">
                        {isMobile && <div className="bento-card-image-container bento-card-image-container--contain" ><PhoneAnimation /></div>}
                        {!isMobile && <div className="bento-card-image-container" ><PhoneAnimation /></div>}
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{HC_MOCK_CARDS[0].title}</div>
                                <div className="bento-card-subtitle">{HC_MOCK_CARDS[0].subtitle}</div>
                            </div>
                        </div>
                    </div>

                    <div className="bento-card hc-card-2">
                        <div className="bento-card-image-container"><img src='./bento-images/Untitled.jpeg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title" >{HC_MOCK_CARDS[1].title}</div>
                                <div className="bento-card-subtitle" >{HC_MOCK_CARDS[1].subtitle}</div>
                            </div>
                        </div>
                    </div>

                    <div className="bento-card hc-card-3">
                        <div className="bento-card-image-container"><ChartInterative /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{HC_MOCK_CARDS[2].title}</div>
                                <div className="bento-card-subtitle">{HC_MOCK_CARDS[2].subtitle}</div>
                            </div>
                        </div>
                    </div>

                    {/* hc-card-4: small accent (top-right, row 2) */}
                    <div className="bento-card hc-card-4">
                        <div className="bento-card-image-container"><SpeedInteractive /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{'< 2 hrs'}</div>
                                <div className="bento-card-subtitle">avg. urgent shift fill time</div>
                            </div>
                        </div>
                    </div>

                    {/* hc-card-5 to hc-card-8: bottom row feature accents */}
                    <div className="bento-card hc-card-5">
                        <div className="bento-card-image-container"><CredentialTracker/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">Credential Tracking</div>
                                <div className="bento-card-subtitle">Licences verified and expiry-monitored automatically</div>
                            </div>
                        </div>
                    </div>

                    <div className="bento-card hc-card-6">
                        <div className="bento-card-image-container"><img src='./bento-images/healthcare/multiple-facilities.jpg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">Multi-Site</div>
                                <div className="bento-card-subtitle">Every care facility managed from one dashboard</div>
                            </div>
                        </div>
                    </div>

                    <div className="bento-card hc-card-7">
                        <div className="bento-card-image-container"><img src='./bento-images/workypayroll.jpg' style={{transform:'scale(0.9)', marginTop:'10px', marginRight:'-50px'}}/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">Zero Disputes</div>
                                <div className="bento-card-subtitle">Payroll-ready timesheets, auto-generated after every shift</div>
                            </div>
                        </div>
                    </div>

                    <div className="bento-card hc-card-8">
                        <div className="bento-card-image-container"><img src='./bento-images/locations.png' style={{transform:'scale(1.2)', marginTop:'-20px', marginRight:'100px'}}/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">Workforce Tracking</div>
                                <div className="bento-card-subtitle">Track workforce activity across every shift worked</div>
                            </div>
                        </div>
                    </div>
                </div>
        </div>
    );
};

export { HealthcareBentoGrid };
